import * as React from 'react';
import {FlatList, StyleSheet, View} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import {withGlobalize, WithGlobalizeProps} from 'react-native-globalize';
import BackSubheader from '../../elements/BackSubheader';
import CheckoutBar from '../../elements/orderAhead/CheckoutBar';
import type {LocationType} from 'src/types/Location';
import type {ProductCategory} from 'src/types/ProductCategory';
import {CategoryType} from 'src/types/ProductCategory';
import MenuService from 'src/services/MenuService';
import Styles from '../../Styles';
import KeyboardAvoidingView from '../../elements/365KeyboardAvoidingView';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import AccountConstants from 'src/constants/AccountConstants';
import TransactionStore from 'src/stores/TransactionStore';
import SearchProduct from '../../elements/cart/SearchProduct';
import SearchField from '../../elements/cart/SearchField';
import ProductCategories from '../../elements/cart/ProductCategories';
import type {MenuCategoryType, MenuProductType} from 'src/types/Menu';
import CartTypes from 'src/constants/cart/CartTypes';
import RecentOrdersButton from '../../elements/orderAhead/RecentOrdersButton';
import Localized from 'src/constants/AppStrings';
import {alertError} from '../../helpers/AlertHelper';
import SubMenu from '../../elements/orderAhead/SubMenu';
import MainConsumerContext from '../../MainConsumerContext';
import Events from 'src/logging/Events';
import MenuActions from 'src/actions/MenuActions';
import uuid from 'src/nativeModules/UUID';

type MenuScreenProps = WithGlobalizeProps & {
  location: LocationType;
};
type MenuScreenState = {
  items: Array<MenuProductType | MenuCategoryType>;
  categories: Array<ProductCategory>;
  selectedCategory: ProductCategory;
  searchValue: string;
};

class MenuScreen extends React.Component<MenuScreenProps, MenuScreenState> {
  pickupTimeout: any;
  static contextType = MainConsumerContext;

  constructor(props: MenuScreenProps) {
    super(props);
    this.getMenuCategories = this.getMenuCategories.bind(this);
    this.getProducts = this.getProducts.bind(this);
    this.renderItem = this.renderItem.bind(this);
    this.onSearchChanged = this.onSearchChanged.bind(this);
    this.goToProductDetail = this.goToProductDetail.bind(this);
    this.goToCart = this.goToCart.bind(this);
    this.onCategoryPress = this.onCategoryPress.bind(this);
    this.productAdded = this.productAdded.bind(this);
    this.onTransactionStoreChanged = this.onTransactionStoreChanged.bind(this);
    this.goToRecentOrders = this.goToRecentOrders.bind(this);

    const pickupTime = TransactionStore.getPickupTime();
    if (pickupTime) {
      MenuService.setCurrentMenu(pickupTime);
    }
    const categories = this.getMenuCategories();
    const selectedCategory = categories.length > 0 ? categories[0] : null;

    const items =
      selectedCategory.type === CategoryType.Product
        ? []
        : selectedCategory.type === CategoryType.TopMenu
        ? this.getSubMenus(selectedCategory.key)
        : this.getProducts(selectedCategory.key);
    this.state = {
      items,
      categories,
      selectedCategory,
      searchValue: '',
    };
    this.pickupTimeout = TransactionStore.getPickupTime();
    TransactionStore.addChangeListener(this.onTransactionStoreChanged);
  }

  componentWillUnmount() {
    TransactionStore.removeChangeListener(this.onTransactionStoreChanged);
  }

  onTransactionStoreChanged() {
    const pickupTime = TransactionStore.getPickupTime();

    if (pickupTime) {
      MenuService.setCurrentMenu(pickupTime);
      const categories = this.getMenuCategories();
      const selectedCategory: ProductCategory = categories?.[0];
      const items =
        selectedCategory.type === CategoryType.Product
          ? []
          : selectedCategory.type === CategoryType.TopMenu
          ? this.getSubMenus(selectedCategory.key)
          : this.getProducts(selectedCategory.key);
      this.setState({
        categories,
        items,
      });
    }
  }

  goToCart() {
    NavActions.push(AppRoutes.Scan, {
      marketName: this.props.location.name,
      locationId: this.props.location.id,
      locationType: AccountConstants.SOS_LOCATION_TYPE,
      beaconId: this.props.location.broadcastid ?? this.props.location.id,
      cartType: CartTypes.OrderAhead,
      location: this.props.location,
      showPreparationMethod: this.props.location.hasdiningpreference,
      defaultPreparationMethod: this.props.location.diningpreference,
    });
  }

  goToProductDetail = async (product: MenuProductType) => {
    try {
      this.context.actions.showSpinner();
      await MenuActions.getModifiers(this.props.location.id, product);
      NavActions.push(AppRoutes.ProductDetail, {
        strings: Localized,
        alertError: alertError,
        location: this.props.location,
        product,
        productAdded: this.productAdded,
      });
    } catch (error) {
      const guid = await uuid.getRandomUUID();
      Events.Error.trackEvent(
        'Exception',
        'MenuScreen:goToProductDetail',
        error.message ? error.message : error.toString(),
        guid,
      );
    } finally {
      this.context.actions.hideSpinner();
    }
  };

  goToRecentOrders() {
    NavActions.push(AppRoutes.RecentOrders, {
      location: this.props.location,
    });
  }

  onSearchChanged(text: string) {
    const {selectedCategory} = this.state;
    const items =
      selectedCategory.type === CategoryType.Product
        ? []
        : selectedCategory.type === CategoryType.TopMenu
        ? MenuService.searchSubMenus(text, selectedCategory.key)
        : MenuService.searchProducts(text, selectedCategory.key);
    this.setState({
      items,
      searchValue: text,
    });
  }

  onCategoryPress(category: ProductCategory) {
    if (category.type === CategoryType.Product) {
      const product: MenuProductType | null = MenuService.getFirstProduct(
        category.key,
      );

      if (product) {
        this.goToProductDetail(product);
      }
    } else {
      this.setState(
        {
          selectedCategory: category,
        },
        () => this.onSearchChanged(this.state.searchValue),
      );
    }
  }

  productAdded() {
    // Clear the search
    this.onSearchChanged('');
  }

  getMenuCategories() {
    const categories = MenuService.getMenuCategories().map((category) => {
      const isProduct = category.isautoadd === 'Y' && category.products?.length === 1;
      const type = category.name === CategoryType.All
          ? CategoryType.All
          : isProduct
          ? CategoryType.Product
          : category.products.length > 0
          ? CategoryType.Menu
          : CategoryType.TopMenu;
      return {
        key: category.name === CategoryType.All ? -1 : category.id,
        name: category.longname,
        iconName: isProduct ? 'plus' : 'bars',
        type,
      };
    });
    return categories;
  }

  getProducts(menuId: number | string = -1) {
    return MenuService.searchProducts('', menuId);
  }

  getSubMenus(menuId: number | string = -1) {
    return MenuService.searchSubMenus('', menuId);
  }

  renderItem({item}) {
    if (item.parent) {
      return <SubMenu menu={item} onProductPress={this.goToProductDetail} />;
    }

    return (
      <SearchProduct
        onPress={() => this.goToProductDetail(item)}
        name={item.name}
        price={parseFloat(item.price)}
        imageUrl={item.largeimageurl}
      />
    );
  }

  render() {
    const {selectedCategory} = this.state;
    const key =
      selectedCategory.type !== CategoryType.Product
        ? selectedCategory.key
        : '';
    return (
      <BackSubheader
        title={Localized.Labels.menu}
        rightView={<RecentOrdersButton onPress={this.goToRecentOrders} />}
      >
        <KeyboardAvoidingView behavior="height" insideTab>
          <View style={styles.content}>
            <SearchField
              onChangeText={this.onSearchChanged}
              strings={Localized}
              value={this.state.searchValue}
            />
            <ProductCategories
              categories={this.state.categories}
              selectedCategory={key}
              style={styles.categories}
              onCategoryPress={this.onCategoryPress}
            />
            <FlatList
              data={this.state.items}
              renderItem={this.renderItem}
              keyExtractor={(item, index) =>
                item.guid
                  ? `${index}:${item.guid}`
                  : `${index}:${item.level}${item.parent}${item.id}`
              }
              style={styles.list}
            />
          </View>
          <CheckoutBar
            location={this.props.location}
            strings={Localized}
            onPress={this.goToCart}
          />
        </KeyboardAvoidingView>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  list: {
    marginTop: Styles.Spacing.m2,
  },
  categories: {
    marginVertical: Styles.Spacing.m2,
  },
});
export default withForwardedNavigationParams()(withGlobalize(MenuScreen));
